import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api';
import { toast } from '../utils/toast';
import { extractApiError } from '../utils/apiError';

export type ApiTaskPriority = 'low' | 'medium' | 'high';
export type ApiTaskStatus = 'to_do' | 'in_progress' | 'complete';

export interface ApiAssignee {
  id: number;
  username?: string | null;
  full_name?: string | null;
  email?: string;
  [key: string]: any;
}

export interface ApiTask {
  uuid: string;
  project_uuid: string;
  title: string;
  description: string;
  cover_image: string | null;
  attachments: string[];
  external_link: string;
  start_date: string | null;
  end_date: string | null;
  assignees: ApiAssignee[];
  quantity: number;
  budget_allocation: string;
  priority: ApiTaskPriority;
  status: ApiTaskStatus;
  created_by?: any;
  created_at: string;
  updated_at: string;
}

export interface TaskStatusCounts {
  all?: number;
  to_do?: number;
  in_progress?: number;
  complete?: number;
}

export interface PaginatedTasksResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ApiTask[];
  status_counts?: TaskStatusCounts;
}

export interface GetTasksParams {
  projectUuid: string;
  page?: number;
  page_size?: number;
  status?: 'all' | 'to_do' | 'in_progress' | 'complete' | string;
}

export const useGetTasks = (params: GetTasksParams, options: { enabled?: boolean } = {}) => {
  return useQuery<PaginatedTasksResponse>({
    queryKey: ['projects', params.projectUuid, 'tasks', params],
    enabled: options.enabled !== false && !!params.projectUuid,
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.set('page', String(params.page));
      if (params.page_size) searchParams.set('page_size', String(params.page_size));
      if (params.status && params.status !== 'all') {
        searchParams.set('status', params.status);
      }
      const qs = searchParams.toString();
      const { data } = await apiClient.get<PaginatedTasksResponse>(
        `/projects/${params.projectUuid}/tasks/${qs ? `?${qs}` : ''}`,
      );
      return data;
    },
  });
};

export interface CreateTaskPayload {
  title: string;
  description?: string;
  cover_image?: string | null;
  attachments?: string[];
  external_link?: string;
  start_date?: string | null;
  end_date?: string | null;
  assignee_ids?: number[];
  quantity?: number;
  budget_allocation?: string;
  priority?: ApiTaskPriority;
  status?: ApiTaskStatus;
}

export const useCreateTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectUuid,
      payload,
    }: {
      projectUuid: string;
      payload: CreateTaskPayload;
    }) => {
      const { data } = await apiClient.post<
        {
          success?: boolean;
          message?: string;
          data?: ApiTask;
        } & ApiTask
      >(`/projects/${projectUuid}/tasks/`, payload);

      // Defensively support both wrapped and direct responses
      return data.data ?? data;
    },
    onSuccess: (_data, { projectUuid }) => {
      toast.success('Task created successfully');
      queryClient.invalidateQueries({ queryKey: ['projects', projectUuid, 'tasks'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to create task'));
    },
  });
};

export interface UpdateTaskPayload extends Partial<CreateTaskPayload> {
  clear_cover_image?: boolean;
}

export const useUpdateTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      projectUuid,
      taskUuid,
      payload,
    }: {
      projectUuid: string;
      taskUuid: string;
      payload: UpdateTaskPayload;
    }) => {
      const { data } = await apiClient.patch<
        {
          success?: boolean;
          message?: string;
          data?: ApiTask;
        } & ApiTask
      >(`/projects/${projectUuid}/tasks/${taskUuid}/`, payload);

      return data.data ?? data;
    },
    onSuccess: (_data, { projectUuid }) => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectUuid, 'tasks'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to update task'));
    },
  });
};

export const useDeleteTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectUuid, taskUuid }: { projectUuid: string; taskUuid: string }) => {
      await apiClient.delete(`/projects/${projectUuid}/tasks/${taskUuid}/`);
      return { projectUuid, taskUuid };
    },
    onSuccess: ({ projectUuid }) => {
      toast.success('Task deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['projects', projectUuid, 'tasks'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to delete task'));
    },
  });
};
